import { JOB_STATUS } from '@/lib/db-constants';
import type { Job } from '@/lib/db-row-types';
import type { JobWithDetails } from './jobs';

export const DILIGENCE_PDF_EXPORT_JOB_TYPE = 'diligence_pdf_export' as const;
export const DILIGENCE_PPTX_EXPORT_JOB_TYPE = 'diligence_pptx_export' as const;
export const DILIGENCE_XLSX_EXPORT_JOB_TYPE = 'diligence_xlsx_export' as const;

export type DiligenceExportJobType =
  | typeof DILIGENCE_PDF_EXPORT_JOB_TYPE
  | typeof DILIGENCE_PPTX_EXPORT_JOB_TYPE
  | typeof DILIGENCE_XLSX_EXPORT_JOB_TYPE;

/** Every export type the jobs menu and its reload query list. */
export const DILIGENCE_EXPORT_JOB_TYPES: readonly DiligenceExportJobType[] = [
  DILIGENCE_PDF_EXPORT_JOB_TYPE,
  DILIGENCE_PPTX_EXPORT_JOB_TYPE,
  DILIGENCE_XLSX_EXPORT_JOB_TYPE,
];

/** The jobs menu shows recent export activity (completed or failed) for 48h. */
export const DILIGENCE_EXPORT_MENU_WINDOW_MS = 48 * 60 * 60 * 1000;
/**
 * How long an exported file stays downloadable: the bucket's own `exports/`
 * retention (`infra/app/gcs.py`). Shorter hides files that exist, longer offers 410s.
 */
export const DILIGENCE_EXPORT_LIFETIME_MS = 365 * 24 * 60 * 60 * 1000;

// Only the fields the frontend actually reads (`site_id` for the per-site
// lookup, `filename` for menu display) are validated and returned. The full
// backend payload also carries type-specific fields (e.g. `source_paths` for
// PDF, `criteria_results` for PPTX) plus `workspace_id`, but nothing here
// consumes them — asserting their shape would mislead readers into thinking
// they're checked when they aren't.
export interface DiligenceExportPayload {
  job_type: DiligenceExportJobType;
  /**
   * `layer_rows.id` — a serial int, not a string. The PDF and PPTX export types
   * normalize to this key server-side (the PPTX payload from its request's
   * `layer_row_id`), and it may be `null` — a PPTX export kicked off without a
   * resolvable site, or an Excel export of several sites, still belongs in the
   * jobs menu, it just never matches a per-site "Download (date)" button.
   */
  site_id: number | null;
  /** The backend stores `str | None` — falls back to `job.name` at call sites. */
  filename: string | null;
}

export interface DiligenceExportResult {
  gcs_path: string;
  generated_at: string;
  /** Present only on PPTX exports whose on-demand synthesis regeneration failed; the deck still
   * builds and completes, but the completion toast surfaces a warning. */
  synthesis_status?: 'ok' | 'failed';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isDiligenceExportJobType(value: unknown): value is DiligenceExportJobType {
  return DILIGENCE_EXPORT_JOB_TYPES.includes(value as DiligenceExportJobType);
}

/**
 * Reads a job's `payload` as a diligence-export payload, or null if it isn't
 * one. Classification depends only on `job_type` — a missing/malformed
 * `site_id` still counts as a diligence export (normalized to `null`), so a
 * null-site PPTX job isn't silently dropped from the jobs menu.
 */
export function getDiligenceExportPayload(
  job: Pick<Job, 'payload'>
): DiligenceExportPayload | null {
  const { payload } = job;
  if (!isRecord(payload)) return null;
  if (!isDiligenceExportJobType(payload.job_type)) return null;
  return {
    job_type: payload.job_type,
    site_id: typeof payload.site_id === 'number' ? payload.site_id : null,
    filename: typeof payload.filename === 'string' ? payload.filename : null,
  };
}

/** Reads a job's `result` as a diligence-export result, or null if absent/malformed. */
export function getDiligenceExportResult(job: Pick<Job, 'result'>): DiligenceExportResult | null {
  const { result } = job;
  if (!isRecord(result)) return null;
  if (typeof result.gcs_path !== 'string' || typeof result.generated_at !== 'string') return null;
  const synthesisStatus =
    result.synthesis_status === 'ok' || result.synthesis_status === 'failed'
      ? result.synthesis_status
      : undefined;
  return {
    gcs_path: result.gcs_path,
    generated_at: result.generated_at,
    synthesis_status: synthesisStatus,
  };
}

export function isDiligenceExportJob(job: Pick<Job, 'payload'>): boolean {
  return getDiligenceExportPayload(job) !== null;
}

/**
 * Completed/failed diligence exports (either job type) within `windowMs` of
 * their terminal timestamp — the jobs-menu recent-activity window (48h). Uses
 * the render's `generated_at` when present (completed exports), falling back
 * to `updated_at` for failures, which never get a `result`.
 */
export function getRecentDiligenceExports(
  jobs: JobWithDetails[],
  windowMs: number = DILIGENCE_EXPORT_MENU_WINDOW_MS,
  now: number = Date.now()
): JobWithDetails[] {
  return jobs.filter(job => {
    if (job.status !== JOB_STATUS.COMPLETED && job.status !== JOB_STATUS.FAILED) return false;
    if (!isDiligenceExportJob(job)) return false;
    const referenceTime = getDiligenceExportResult(job)?.generated_at ?? job.updated_at;
    return now - new Date(referenceTime).getTime() <= windowMs;
  });
}

/**
 * Newest completed, non-expired export of the given job type for a site —
 * feeds the "Download (date)" button in the site report modal (PDF) and the
 * Overview tab (PPTX), retrievable for the file's full 30-day lifetime.
 * Scoped to `jobType` so the PDF and PPTX buttons never cross-offer each
 * other's files.
 */
export function getLatestCompletedDiligenceExportForSite(
  jobs: JobWithDetails[],
  siteId: number,
  jobType: DiligenceExportJobType,
  lifetimeMs: number = DILIGENCE_EXPORT_LIFETIME_MS,
  now: number = Date.now()
): JobWithDetails | null {
  let latest: JobWithDetails | null = null;
  let latestGeneratedAtMs = -Infinity;

  for (const job of jobs) {
    if (job.status !== JOB_STATUS.COMPLETED) continue;
    const payload = getDiligenceExportPayload(job);
    if (!payload || payload.job_type !== jobType || payload.site_id !== siteId) continue;
    const result = getDiligenceExportResult(job);
    if (!result) continue;

    const generatedAtMs = new Date(result.generated_at).getTime();
    if (now - generatedAtMs > lifetimeMs) continue;
    if (generatedAtMs > latestGeneratedAtMs) {
      latest = job;
      latestGeneratedAtMs = generatedAtMs;
    }
  }

  return latest;
}
